import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';

const btnPrimary = { padding: '8px 16px', background: '#534AB7', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer' };
const btnGhost = { padding: '7px 12px', background: 'none', border: '1px solid #e8e8e6', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#555' };
const btnGreen = { padding: '7px 14px', background: 'none', border: '1px solid #27ae60', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#27ae60', fontWeight: 500 };
const btnRed = { padding: '7px 14px', background: 'none', border: '1px solid #c0392b', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#c0392b' };
const inp = { width: '100%', padding: '9px 12px', border: '1px solid #e8e8e6', borderRadius: 8, fontSize: 14, marginBottom: 12, boxSizing: 'border-box', outline: 'none', display: 'block' };

const CONTACT_STATUS_COLORS = {
  pending: '#aaa',
  searching: '#3498db',
  sending: '#9b59b6',
  sent: '#27ae60',
  replied: '#27ae60',
  error: '#c0392b',
  not_found: '#e67e22',
  flood_wait: '#e67e22',
  blocked: '#c0392b',
};

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [view, setView] = useState('list');
  const [selected, setSelected] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [logs, setLogs] = useState([]);
  const logsRef = useRef();
  const contactsTimer = useRef(null);
  const logsTimer = useRef(null);

  const [form, setForm] = useState({
    name: '', message_template: '', targets_text: '',
    account_ids: [], agent_id: '', delay_from: 60, delay_to: 180, daily_limit: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editId, setEditId] = useState(null);
  const [checkingContacts, setCheckingContacts] = useState(false);
  const [checkResult, setCheckResult] = useState(null);

  const load = async () => {
    const [c, ag, ac] = await Promise.all([api.get('/campaigns'), api.get('/agents'), api.get('/accounts')]);
    setCampaigns(c.data); setAgents(ag.data); setAccounts(ac.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  // Автообновление контактов
  useEffect(() => {
    if (view === 'contacts' && selected) {
      const loadContacts = async () => {
        const { data } = await api.get(`/campaigns/${selected.id}/contacts`);
        setContacts(data);
      };
      loadContacts();
      contactsTimer.current = setInterval(loadContacts, 2000);
    }
    return () => clearInterval(contactsTimer.current);
  }, [view, selected?.id]);

  // Логи из journalctl через API
  useEffect(() => {
    if (showLogs && selected) {
      const loadLogs = async () => {
        try {
          const { data } = await api.get(`/campaigns/${selected.id}/logs`);
          setLogs(data.logs || []);
          setTimeout(() => logsRef.current?.scrollTo(0, logsRef.current.scrollHeight), 100);
        } catch {}
      };
      loadLogs();
      logsTimer.current = setInterval(loadLogs, 2000);
    }
    return () => clearInterval(logsTimer.current);
  }, [showLogs, selected?.id]);

  const checkContacts = async () => {
    if (!form.targets_text.trim()) { setError('Введите контакты для проверки'); return; }
    if (!form.account_ids.length) { setError('Выберите аккаунт для проверки'); return; }
    setCheckingContacts(true); setCheckResult(null); setError('');
    try {
      const { data } = await api.post('/campaigns/check-contacts', {
        targets_text: form.targets_text,
        account_id: form.account_ids[0],
      });
      setCheckResult(data);
      // Автоматически оставляем только валидные
      setForm(f => ({ ...f, targets_text: data.valid.join('\n') }));
    } catch (e) { setError(e.response?.data?.detail || e.message || 'Ошибка проверки ' + e.response?.status); console.error('check-contacts error:', e.response?.status, e.response?.data); }
    setCheckingContacts(false);
  };

  const create = async () => {
    setError('');
    if (!form.name) { setError('Введите название'); return; }
    if (!form.message_template) { setError('Введите текст'); return; }
    if (!editId && !form.targets_text) { setError('Добавьте контакты'); return; }
    if (!form.account_ids.length) { setError('Выберите аккаунт'); return; }
    setLoading(true);
    try {
      if (editId) {
        await api.put(`/campaigns/${editId}`, { ...form, agent_id: form.agent_id ? parseInt(form.agent_id) : null });
      } else {
        await api.post('/campaigns', { ...form, agent_id: form.agent_id ? parseInt(form.agent_id) : null });
      }
      setView('list');
      setEditId(null);
      setForm({ name: '', message_template: '', targets_text: '', account_ids: [], agent_id: '', delay_from: 60, delay_to: 180, daily_limit: 10 });
      await load();
    } catch (e) { setError(e.response?.data?.detail || 'Ошибка'); }
    setLoading(false);
  };

  const startCampaign = async (id) => { await api.post(`/campaigns/${id}/start`); await load(); };

  const editCampaign = (c) => {
    setForm({
      name: c.name,
      message_template: c.message_template,
      targets_text: '',
      account_ids: c.account_ids || [],
      agent_id: c.agent_id || '',
      delay_from: c.delay_from,
      delay_to: c.delay_to,
      daily_limit: c.daily_limit,
    });
    setEditId(c.id);
    setView('create');
  };

  const copyCampaign = (c) => {
    setForm({
      name: c.name + ' (копия)',
      message_template: c.message_template,
      targets_text: '',
      account_ids: c.account_ids || [],
      agent_id: c.agent_id || '',
      delay_from: c.delay_from,
      delay_to: c.delay_to,
      daily_limit: c.daily_limit,
    });
    setEditId(null);
    setView('create');
  };

  const pauseCampaign = async (id) => { await api.post(`/campaigns/${id}/pause`); await load(); };
  const deleteCampaign = async (id) => { if (!window.confirm('Удалить рассылку?')) return; await api.delete(`/campaigns/${id}`); await load(); };

  const openContacts = async (camp) => {
    setSelected(camp);
    setContactsLoading(true);
    const { data } = await api.get(`/campaigns/${camp.id}/contacts`);
    setContacts(data);
    setContactsLoading(false);
    setView('contacts');
  };

  const toggleAccount = (id) => setForm(f => ({
    ...f, account_ids: f.account_ids.includes(id) ? f.account_ids.filter(x => x !== id) : [...f.account_ids, id]
  }));

  if (view === 'contacts' && selected) {
    const currentCamp = campaigns.find(c => c.id === selected.id) || selected;
    return (
      <div style={{ padding: 32 }}>
        <button onClick={() => setView('list')} style={btnGhost}>← Назад</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, margin: '16px 0 8px' }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{currentCamp.name}</h2>
          <span style={{ fontSize: 13, padding: '4px 12px', background: '#f0effe', color: '#534AB7', borderRadius: 20 }}>
            {currentCamp.status_text || currentCamp.status}
          </span>
          <button onClick={() => setShowLogs(!showLogs)} style={{ ...btnGhost, marginLeft: 'auto', borderColor: showLogs ? '#534AB7' : '#e8e8e6', color: showLogs ? '#534AB7' : '#555' }}>
            📋 {showLogs ? 'Скрыть логи' : 'Логи'}
          </button>
        </div>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>
          Отправлено: <b>{currentCamp.sent}</b> / {currentCamp.total} · Ответили: <b style={{ color: '#27ae60' }}>{currentCamp.replied}</b> · Ошибки: <b style={{ color: '#c0392b' }}>{currentCamp.errors}</b>
        </div>

        {/* Прогресс бар */}
        <div style={{ height: 8, background: '#f0f0ee', borderRadius: 4, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ height: '100%', background: '#534AB7', borderRadius: 4, width: `${currentCamp.total ? currentCamp.sent / currentCamp.total * 100 : 0}%`, transition: 'width 0.5s' }} />
        </div>

        {showLogs && (
          <div style={{ background: '#1a1a2e', borderRadius: 8, padding: 12, marginBottom: 16, maxHeight: 240, overflow: 'auto', fontFamily: 'monospace', fontSize: 12 }} ref={logsRef}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#4ade80', fontSize: 11 }}>📋 Логи рассылки (обновляются каждые 2 сек)</span>
              <button onClick={() => { navigator.clipboard.writeText(logs.join('\n')); }} style={{ background: 'none', border: '1px solid #4ade80', borderRadius: 4, color: '#4ade80', fontSize: 11, padding: '2px 8px', cursor: 'pointer' }}>
                📋 Копировать
              </button>
            </div>
            {logs.length === 0 && <div style={{ color: '#666' }}>Логов пока нет...</div>}
            {logs.map((log, i) => (
              <div key={i} style={{ color: log.includes('ERROR') || log.includes('❌') ? '#f87171' : log.includes('OK') || log.includes('✅') ? '#4ade80' : log.includes('⏱') || log.includes('FloodWait') ? '#fbbf24' : '#a3a3a3', marginBottom: 2, lineHeight: 1.5 }}>
                {log}
              </div>
            ))}
          </div>
        )}
          <div style={{ color: '#aaa', textAlign: 'center', padding: 40 }}>Загружаем контакты...</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {contacts.map(c => (
              <div key={c.id} style={{ background: '#fff', border: '1px solid #e8e8e6', borderRadius: 8, padding: '10px 14px', fontSize: 13 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontFamily: 'monospace', color: '#555', minWidth: 130 }}>{c.target}</span>
                  {c.first_name && <span style={{ color: '#534AB7', fontWeight: 500 }}>{c.first_name}</span>}
                  <span style={{ flex: 1 }} />
                  <span style={{ color: CONTACT_STATUS_COLORS[c.status] || '#aaa', fontSize: 12 }}>
                    {c.status_text || c.status}
                  </span>
                  {c.retry_at && c.status === 'flood_wait' && (
                    <span style={{ fontSize: 11, color: '#e67e22' }}>
                      повтор в {new Date(c.retry_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                {c.error && (
                  <div style={{ marginTop: 5, fontSize: 11, color: '#c0392b', background: '#fdecea', padding: '4px 8px', borderRadius: 5 }}>
                    ⚠️ {c.error}
                  </div>
                )}
              </div>
            ))}
            {contacts.length === 0 && <div style={{ color: '#aaa', textAlign: 'center', padding: 40 }}>Контактов нет</div>}
          </div>
        )}
      </div>
    );
  }

  if (view === 'create') return (
    <div style={{ padding: 32, maxWidth: 700 }}>
      <button onClick={() => setView('list')} style={btnGhost}>← Назад</button>
      <h2 style={{ fontSize: 18, fontWeight: 600, margin: '16px 0 20px' }}>{editId ? '✏️ Редактировать рассылку' : 'Новая рассылка'}</h2>

      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Название</label>
      <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inp} placeholder="Холодная рассылка Авито" />

      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Текст первого сообщения</label>
      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 6 }}>Спинтакс: {'{Привет|Здравствуйте}'} · Имя: {'{имя}'}</div>
      <textarea value={form.message_template} onChange={e => setForm(f => ({ ...f, message_template: e.target.value }))}
        rows={5} style={{ ...inp, resize: 'vertical' }}
        placeholder="{Привет|Добрый день}! Вижу вы интересовались продвижением на Авито. Есть пара минут — покажу как получать больше заявок?" />

      {!editId && (<>
      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Контакты</label>
      <div style={{ fontSize: 12, color: '#aaa', marginBottom: 6 }}>Номера или @username — каждый с новой строки</div>
      <textarea value={form.targets_text} onChange={e => setForm(f => ({ ...f, targets_text: e.target.value }))}
        rows={6} style={{ ...inp, fontFamily: 'monospace', fontSize: 13, resize: 'vertical' }}
        placeholder={"+79001234567\n89001234567\n@username"} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <span style={{ fontSize: 12, color: '#888' }}>
          {form.targets_text.trim().split('\n').filter(l => l.trim()).length} контактов
        </span>
        <button onClick={checkContacts} disabled={checkingContacts} style={{ ...btnGhost, fontSize: 12, padding: '5px 10px', borderColor: '#3498db', color: '#3498db' }}>
          {checkingContacts ? '⏳ Проверяем...' : '🔍 Проверить базу'}
        </button>
        {checkResult && (
          <span style={{ fontSize: 12 }}>
            <span style={{ color: '#27ae60' }}>✅ {checkResult.valid_count}</span>
            {' · '}
            <span style={{ color: '#e67e22' }}>👻 {checkResult.not_found_count}</span>
            {checkResult.already_sent_count > 0 && <span style={{ color: '#c0392b' }}> · 🔄 уже писали: {checkResult.already_sent_count}</span>}
            {' из '}{checkResult.total}
          </span>
        )}
      </div>
      </>)}

      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 8 }}>Аккаунты</label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button onClick={() => setForm(f => ({ ...f, account_ids: accounts.filter(a => a.is_active).map(a => a.id) }))} style={btnGhost}>Выбрать все</button>
        <button onClick={() => setForm(f => ({ ...f, account_ids: [] }))} style={btnGhost}>Снять</button>
      </div>
      <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
        {accounts.filter(a => a.is_active).map(a => (
          <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: form.account_ids.includes(a.id) ? '#f0effe' : '#fff', border: `1px solid ${form.account_ids.includes(a.id) ? '#534AB7' : '#e8e8e6'}`, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={form.account_ids.includes(a.id)} onChange={() => toggleAccount(a.id)} />
            <span>{a.first_name || a.name}</span>
            {a.warmup_enabled && <span style={{ fontSize: 11, color: '#f39c12', marginLeft: 4 }}>🌡 День {a.warmup_day}</span>}
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#aaa' }}>лимит {a.daily_limit}/сут</span>
          </label>
        ))}
        {accounts.filter(a => a.is_active).length === 0 && <div style={{ color: '#aaa', fontSize: 13 }}>Нет активных аккаунтов</div>}
      </div>

      <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 8 }}>ИИ агент (продолжает диалог после ответа)</label>
      <select value={form.agent_id} onChange={e => setForm(f => ({ ...f, agent_id: e.target.value }))} style={inp}>
        <option value="">Без агента (только первое сообщение)</option>
        {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Задержка от (сек)</label>
          <input type="number" value={form.delay_from} onChange={e => setForm(f => ({ ...f, delay_from: parseInt(e.target.value) }))} style={{ ...inp, marginBottom: 0 }} min={15} />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Задержка до (сек)</label>
          <input type="number" value={form.delay_to} onChange={e => setForm(f => ({ ...f, delay_to: parseInt(e.target.value) }))} style={{ ...inp, marginBottom: 0 }} min={15} />
        </div>
        <div>
          <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 6 }}>Лимит в сутки / аккаунт</label>
          <input type="number" value={form.daily_limit} onChange={e => setForm(f => ({ ...f, daily_limit: parseInt(e.target.value) }))} style={{ ...inp, marginBottom: 0 }} min={1} max={200} />
        </div>
      </div>

      {error && <div style={{ color: '#c0392b', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={create} disabled={loading} style={btnPrimary}>{loading ? '...' : editId ? 'Сохранить' : 'Создать рассылку'}</button>
        <button onClick={() => { setView('list'); setEditId(null); }} style={btnGhost}>Отмена</button>
      </div>
    </div>
  );

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>Рассылки</h1>
        <button onClick={() => setView('create')} style={btnPrimary}>+ Новая рассылка</button>
      </div>

      {campaigns.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#aaa', fontSize: 14 }}>Рассылок нет. Создайте первую.</div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {campaigns.map(c => (
          <div key={c.id} style={{ background: '#fff', border: '1px solid #e8e8e6', borderRadius: 10, padding: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{c.name}</div>
                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                  {c.status_text || c.status}
                </div>
              </div>
              {(c.status === 'draft' || c.status === 'paused') && !c.running
                ? <button onClick={() => startCampaign(c.id)} style={btnGreen}>▶ Запустить</button>
                : c.running || c.status === 'running'
                ? <button onClick={() => pauseCampaign(c.id)} style={btnGhost}>⏸ Пауза</button>
                : null}
              <button onClick={() => openContacts(c)} style={btnGhost}>📋 Контакты</button>
              <button onClick={() => { setSelected(c); setShowLogs(true); setView('contacts'); }} style={btnGhost}>📊 Логи</button>
              <button onClick={() => editCampaign(c)} style={btnGhost}>✏️</button>
              <button onClick={() => copyCampaign(c)} style={btnGhost}>📄 Копия</button>
              <button onClick={() => deleteCampaign(c.id)} style={btnRed}>🗑</button>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#888', marginBottom: 4 }}>
                <span>Отправлено: {c.sent} / {c.total}</span>
                <span>Ответили: {c.replied} · Ошибки: {c.errors}</span>
              </div>
              <div style={{ height: 6, background: '#f0f0ee', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#534AB7', borderRadius: 3, width: `${c.total ? c.sent / c.total * 100 : 0}%`, transition: 'width 0.5s' }} />
              </div>
            </div>

            <div style={{ fontSize: 12, color: '#aaa', display: 'flex', gap: 16 }}>
              <span>Задержка: {c.delay_from}–{c.delay_to} сек</span>
              <span>Лимит: {c.daily_limit}/сут</span>
              <span>Аккаунтов: {c.account_ids?.length || 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
